/* eslint-disable react/no-danger */
import { LinkAnchorButton } from "lib/miolo";
import { format } from "date-fns";
import parseDateYmd from "lib/utils/parseDateYmd";
import { default as Head } from "next/head";
import Link from "next/link";
import numeral from "numeral";
import { useAmplitude } from "../../lib/amplitude/Amplitude";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import getBaseUrl from "../../lib/utils/getBaseUrl";
import * as palette from "../palette";

interface IGatedBrazilianRealEstateFund {
  realEstateFund: IBrazilianRealEstateFundTopLevelFragment;
}

export default function GatedBrazilianRealEstateFund({ realEstateFund }: IGatedBrazilianRealEstateFund) {
  const amplitude = useAmplitude();
  const shortDescription = getShortDescription(realEstateFund);

  return (
    <main
      css={{
        display: "grid",
        gridRowGap: 32,
        padding: "48px 0",
      }}
    >
      <Head>
        <title>{`${realEstateFund.tickerSymbolPrefix} | Fundamentei`}</title>

        <meta name="title" content={`${realEstateFund.name} | Fundamentei`} />
        <meta name="description" content={shortDescription} />

        <meta name="og:type" property="og:type" content="website" />
        <meta name="og:site_name" property="og:site_name" content="Fundamentei" />
        <meta name="og:title" property="og:title" content={`${realEstateFund.name} | Fundamentei`} />
        <meta name="og:description" property="og:description" content={shortDescription} />
        <meta
          name="og:image"
          property="og:image"
          content={`${getBaseUrl()}/static/branding/wordmark/White on Blue@4x.jpg`}
        />
        <meta name="og:image:type" property="og:image:type" content="image/jpg" />
        <meta name="og:image:width" property="og:image:width" content="3344" />
        <meta name="og:image:height" property="og:image:height" content="976" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@fundamentei" />
        <meta name="twitter:title" content={`${realEstateFund.name} | Fundamentei`} />
        <meta name="twitter:image" content={`${getBaseUrl()}/static/branding/wordmark/White on Blue@4x.jpg`} />
        <meta name="twitter:image:alt" content="Logotipo do Fundamentei" />

        <link rel="canonical" href={`${getBaseUrl()}/fiis/${realEstateFund.tickerSymbolPrefix}`} key="canonical" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "Corporation",
              name: realEstateFund.name,
              tickerSymbol: realEstateFund.tickerSymbol,
              url: realEstateFund.b3Href,
              description: shortDescription,
            }),
          }}
        />
      </Head>

      <div
        css={{
          display: "grid",
          gridColumnGap: 30,
          gridTemplateColumns: "repeat(12, 1fr)",
          alignItems: "center",
          width: 1140,
          margin: "0px auto",
        }}
      >
        <div
          css={{
            gridColumn: "4 / span 6",
            backgroundColor: "#ffffff",
            borderRadius: 8,
            boxShadow: "0 15px 35px 0 #33333333",
          }}
        >
          <div
            css={{
              padding: 28,
            }}
          >
            <div
              css={{
                display: "grid",
                gridColumnGap: 16,
                gridTemplateColumns: "1fr max-content",
                justifyContent: "space-between",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridRowGap: 8,
                  minWidth: 0,
                }}
              >
                <div
                  css={{
                    minWidth: 0,
                  }}
                >
                  <h1
                    title={realEstateFund.name}
                    css={{
                      margin: 0,
                      overflow: "hidden",
                      color: palette.fiord,
                      fontWeight: "bold",
                      fontSize: 33,
                      lineHeight: 1.33,
                      letterSpacing: 0.7,
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {realEstateFund.name}
                  </h1>

                  {realEstateFund.cnpj && (
                    <span
                      css={{
                        color: palette.lynch,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      {`CNPJ: ${realEstateFund.cnpj}`}
                    </span>
                  )}
                </div>

                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "min-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 6,
                  }}
                >
                  <span
                    css={{
                      color: palette.blueBayoux,
                      fontWeight: "bold",
                      fontSize: 14,
                      lineHeight: 1.33,
                      letterSpacing: 0.7,
                    }}
                  >
                    {realEstateFund.tickerSymbolPrefix}
                  </span>
                </div>
              </div>

              {realEstateFund.primaryLogo && (
                <img
                  role="presentation"
                  src={realEstateFund.primaryLogo.url}
                  onContextMenu={(event) => event.preventDefault()}
                  onMouseDown={(event) => event.preventDefault()}
                  width={158}
                  height={48}
                  alt={realEstateFund.name}
                  draggable={false}
                  css={{
                    objectFit: "contain",
                    objectPosition: "right",
                    userSelect: "none",
                    height: 48,
                  }}
                />
              )}
            </div>

            <div
              css={{
                marginTop: 20,
              }}
            >
              <p
                css={{
                  margin: 0,
                  padding: 0,
                  color: palette.baliHai,
                  fontWeight: 300,
                  fontSize: 14,
                  lineHeight: 1.33,
                  letterSpacing: 1,
                }}
              >
                {shortDescription}
              </p>
            </div>

            <div
              css={{
                marginTop: 20,
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                  gridColumnGap: 24,
                }}
              >
                <div>
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.tundora,
                    }}
                  >
                    Início
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.baliHai,
                      marginTop: 4,
                    }}
                  >
                    {realEstateFund.startOfOperation}
                  </div>
                </div>

                <div>
                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: "bold",
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.tundora,
                    }}
                  >
                    Administrador
                  </div>

                  <div
                    css={{
                      fontSize: 14,
                      fontWeight: 500,
                      lineHeight: 1.33,
                      letterSpacing: 0.5,
                      color: palette.baliHai,
                      marginTop: 4,
                    }}
                  >
                    {realEstateFund.administrator.name}
                  </div>
                </div>
              </div>
            </div>

            <div
              css={{
                marginTop: 20,
                color: palette.lynch,
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridAutoColumns: "max-content",
                  gridAutoFlow: "column",
                  gridColumnGap: 12,
                  color: palette.lynch,
                }}
              >
                {realEstateFund.website && (
                  <>
                    <a
                      title="Site do Fundo"
                      target="_blank"
                      href={realEstateFund.website}
                      rel="noopener noreferrer"
                      onClick={() => {
                        amplitude.logEvent("Click on a link in details box", {
                          tickerSymbol: realEstateFund.tickerSymbolPrefix,
                          title: "Site do Fundo",
                          href: realEstateFund.website,
                        });
                      }}
                      css={{
                        color: palette.link,
                        fontWeight: 500,
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Site do Fundo
                    </a>

                    <span>•</span>
                  </>
                )}

                <a
                  title="Site B3"
                  target="_blank"
                  href={realEstateFund.b3Href}
                  rel="noopener noreferrer"
                  onClick={() => {
                    amplitude.logEvent("Click on a link in details box", {
                      tickerSymbol: realEstateFund.tickerSymbolPrefix,
                      title: "Site B3",
                      href: realEstateFund.b3Href,
                    });
                  }}
                  css={{
                    color: palette.link,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Site B3
                </a>

                <span>•</span>

                <a
                  title="Arquivos"
                  target="_blank"
                  href={realEstateFund.fundosNetHref}
                  rel="noopener noreferrer"
                  onClick={() => {
                    amplitude.logEvent("Click on a link in details box", {
                      tickerSymbol: realEstateFund.tickerSymbolPrefix,
                      title: "Arquivos",
                      href: realEstateFund.fundosNetHref,
                    });
                  }}
                  css={{
                    color: palette.link,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: 1.33,
                    letterSpacing: 0.5,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Arquivos
                </a>
              </div>
            </div>

            {realEstateFund.financials.monthly.length > 0 && (
              <div
                css={{
                  marginTop: 20,
                }}
              >
                <div
                  css={{
                    display: "grid",
                    gridAutoColumns: "max-content",
                    gridAutoFlow: "column",
                    gridColumnGap: 24,
                  }}
                >
                  <div>
                    <div
                      css={{
                        color: palette.tundora,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      Ativos
                    </div>

                    <div
                      css={{
                        marginTop: 4,
                        color: palette.viridian,
                        fontWeight: "bold",
                        fontSize: 14,
                        lineHeight: 1.33,
                        letterSpacing: 0.5,
                      }}
                    >
                      <span>
                        {formatTotalAssets(realEstateFund.financials.monthly[0].totalAssets)} (
                        {format(parseDateYmd(realEstateFund.financials.monthly[0].fiscalDate), "MM/yyyy")})
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div
              css={{
                marginTop: 20,
              }}
            >
              <hr
                css={{
                  border: `solid 1px ${palette.catskillWhite}`,
                }}
              />
            </div>

            <div
              css={{
                marginTop: 20,
                textAlign: "center",
              }}
            >
              <Link href="/sign-up" legacyBehavior={true}>
                <LinkAnchorButton
                  href="/"
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    letterSpacing: 1,
                  }}
                >
                  Quer ter acesso ao site completo? Se cadastre!
                </LinkAnchorButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function formatTotalAssets(totalAssets: number): string {
  const formattedTotalAssets = numeral(totalAssets).format("0 a");
  const roundedTotalAssets = parseFloat(formattedTotalAssets);

  return formattedTotalAssets
    .replace("b", roundedTotalAssets === 1 ? "bilhão" : "bilhões")
    .replace("m", roundedTotalAssets === 1 ? "milhão" : "milhões")
    .replace("k", "mil");
}

function sprintf(templateString: string, a?: string, b?: string, c?: string, d?: string, e?: string, f?: string) {
  const args = [a, b, c, d, e, f];
  let argIndex = 0;

  return templateString.replace(/%s/g, () => {
    // eslint-disable-next-line
    return args[argIndex++] || "";
  });
}

function getShortDescription(realEstateFund: IBrazilianRealEstateFundTopLevelFragment): string {
  if (realEstateFund.financials.monthly.length === 0) {
    return sprintf(
      "O fundo %s é de gestão %s voltado para %s. É administrado pela(o) %s.",
      realEstateFund.name,
      realEstateFund.selfRegulation.management,
      realEstateFund.selfRegulation.mandate,
      realEstateFund.administrator.name,
    );
  }

  return sprintf(
    "O fundo %s é de gestão %s voltado para %s e tem cerca de %s em ativos.",
    realEstateFund.name,
    realEstateFund.selfRegulation.management,
    realEstateFund.selfRegulation.mandate,
    formatTotalAssets(realEstateFund.financials.monthly[0].totalAssets),
    realEstateFund.administrator.name,
  );
}
